/**
 * Custom Swym Wishlist Plus UI.
 *
 * Swym's app embed auto-injects its own buttons; those are hidden in
 * assets/swym-wishlist.css and replaced by the markup in
 * snippets/swym-wishlist-heart.liquid and snippets/swym-header-icon.liquid.
 *
 * Everything here is defensive: until a Swym plan is active the SDK never
 * loads, `window.SwymCallbacks` is simply never drained, and the hearts stay
 * inert rather than throwing. The header link falls back to its own href.
 */
(function () {
  'use strict';

  var HEART_SELECTOR = '[data-swym-heart]';
  var COUNT_SELECTOR = '[data-swym-count]';
  var OPEN_SELECTOR = '[data-swym-open]';

  var swat = null;

  /**
   * Saved products, keyed by product id. A wishlist saves a product, not a
   * size: hearting from a collection card has no size to offer, and the
   * shopper picks one in the drawer when they add to bag. The stored variant
   * is kept only so the item can be removed again.
   * @type {Map<string, {empi: number, epi: number, du: string}>}
   */
  var saved = new Map();

  /** Product data by url, so a card looks its product up only once. */
  var products = new Map();

  /** The size picker currently open, if any. */
  var openPicker = null;

  /** Queue a callback until the Swym SDK has initialised. */
  function onSwym(callback) {
    window.SwymCallbacks = window.SwymCallbacks || [];
    window.SwymCallbacks.push(callback);
  }

  function productKey(empi) {
    return String(empi);
  }

  /** Build a Swym item payload from a heart button's data attributes. */
  function itemFrom(el) {
    return {
      empi: parseInt(el.dataset.empi, 10),
      epi: parseInt(el.dataset.epi, 10),
      du: el.dataset.du,
      dt: el.dataset.dt,
      iu: el.dataset.iu,
      pr: parseInt(el.dataset.pr, 10),
      stk: el.dataset.stk === 'false' ? 0 : 1
    };
  }

  function render() {
    document.querySelectorAll(HEART_SELECTOR).forEach(function (el) {
      var active = saved.has(productKey(el.dataset.empi));
      el.classList.toggle('mb-heart--active', active);
      el.setAttribute('aria-pressed', active ? 'true' : 'false');
    });

    document.querySelectorAll(COUNT_SELECTOR).forEach(function (el) {
      el.textContent = saved.size > 0 ? String(saved.size) : '';
      el.hidden = saved.size === 0;
    });

    // Solid heart in the header once there is something saved.
    document.querySelectorAll('.mb-wishlist-link').forEach(function (el) {
      el.classList.toggle('mb-wishlist-link--has-items', saved.size > 0);
    });
  }

  /** Pull the current list from Swym and repaint every heart. */
  function refresh() {
    if (!swat || typeof swat.fetch !== 'function') return;

    try {
      swat.fetch(function (items) {
        saved = new Map();
        (items || []).forEach(function (item) {
          saved.set(productKey(item.empi), { empi: item.empi, epi: item.epi, du: item.du });
        });
        render();
      });
    } catch (error) {
      // SDK present but not ready — leave the current painted state alone.
    }
  }

  function closePicker() {
    if (openPicker && openPicker.parentNode) openPicker.parentNode.removeChild(openPicker);
    openPicker = null;
  }

  /** Fetch a product once, for its variant list. */
  function loadProduct(url) {
    if (products.has(url)) return Promise.resolve(products.get(url));

    return fetch(url + '.js')
      .then(function (response) {
        return response.ok ? response.json() : null;
      })
      .catch(function () {
        return null;
      })
      .then(function (product) {
        var data = {
          variants: product && Array.isArray(product.variants) ? product.variants : [],
          singleOption: !product || !Array.isArray(product.options) || product.options.length === 1
        };
        products.set(url, data);
        return data;
      });
  }

  /**
   * Ask which size to save. A collection card carries no size control, so
   * without this the first available variant is saved silently — and Swym
   * would then email about a size the shopper never chose.
   */
  function showPicker(el, product) {
    closePicker();

    var host = el.closest('.card-gallery') || el.parentElement;
    if (!host) return;

    var picker = document.createElement('div');
    picker.className = 'mb-size-picker';
    picker.setAttribute('role', 'dialog');
    picker.setAttribute('aria-label', 'Choose a size to save');

    var title = document.createElement('p');
    title.className = 'mb-size-picker__title';
    title.textContent = 'Choose a size';
    picker.appendChild(title);

    var list = document.createElement('div');
    list.className = 'mb-size-picker__list';

    product.variants.forEach(function (variant) {
      var button = document.createElement('button');
      button.type = 'button';
      button.className = 'mb-size-picker__size';
      button.textContent = product.singleOption ? variant.option1 : variant.title;

      // Sold-out sizes stay selectable: saving one is how a shopper asks to
      // hear when it returns.
      if (!variant.available) button.classList.add('mb-size-picker__size--out');

      button.addEventListener('click', function (event) {
        event.preventDefault();
        event.stopPropagation();
        closePicker();
        commit(el, String(variant.id));
      });

      list.appendChild(button);
    });

    picker.appendChild(list);
    host.appendChild(picker);
    openPicker = picker;

    var first = picker.querySelector('.mb-size-picker__size');
    if (first) first.focus();
  }

  function toggle(el) {
    if (!swat) return;

    var key = productKey(el.dataset.empi);

    // Removing needs no size.
    if (saved.has(key)) {
      commit(el, null);
      return;
    }

    // The PDP has its own size control, and the shopper can see which is
    // selected, so that choice stands. Cards have none, so ask.
    if (el.classList.contains('mb-heart--pdp')) {
      commit(el, el.dataset.epi);
      return;
    }

    loadProduct(el.dataset.du).then(function (product) {
      if (product.variants.length > 1) showPicker(el, product);
      else commit(el, el.dataset.epi);
    });
  }

  /**
   * @param {Element} el
   * @param {string|null} variantId - the size chosen, or null when removing
   */
  function commit(el, variantId) {
    if (!swat) return;

    var item = itemFrom(el);
    if (variantId) item.epi = Number(variantId);

    var key = productKey(item.empi);
    var wasActive = saved.has(key);

    // Remove whichever variant was actually saved, which need not be the one
    // this heart happens to sit on.
    var previous = saved.get(key);
    var payload = wasActive && previous ? previous : item;

    // Optimistic paint, reconciled by the refresh in the callback.
    if (wasActive) saved.delete(key);
    else saved.set(key, { empi: item.empi, epi: item.epi, du: item.du });
    render();

    // addToList/deleteFromList take a list id as their first argument and throw
    // "Invalid arguments supplied" when handed an item. addToWishList and
    // removeFromWishList operate on the default list, which is what we want.
    var method = wasActive ? 'removeFromWishList' : 'addToWishList';
    if (typeof swat[method] !== 'function') {
      rollback();
      return;
    }

    function rollback() {
      if (wasActive && previous) saved.set(key, previous);
      else saved.delete(key);
      render();
    }

    try {
      swat[method](
        payload,
        function () {
          refresh();
          document.dispatchEvent(new CustomEvent('wishlist:updated', { detail: { source: 'heart' } }));

          if (!wasActive) {
            var drawer = document.querySelector('wishlist-drawer-component');
            if (drawer && typeof drawer.open === 'function') drawer.open();
          }
        },
        rollback
      );
    } catch (error) {
      // A throw here would otherwise leave the optimistic paint in place,
      // showing a saved heart for something that was never saved.
      rollback();
    }
  }

  /**
   * Open Swym's wishlist UI. The exact entry point depends on which storefront
   * layout the app is configured for, so try each in turn and let the link
   * navigate to its href if none are present.
   */
  function open(event) {
    // Our own drawer, built on the theme's dialog component.
    var drawer = document.querySelector('wishlist-drawer-component');
    if (drawer && typeof drawer.open === 'function') {
      event.preventDefault();
      drawer.open();
      return;
    }
    // Nothing to open — fall through to the anchor's href.
  }

  // Delegated so cards added by pagination, filters or quick view work too.
  document.addEventListener('click', function (event) {
    if (openPicker && !event.target.closest('.mb-size-picker')) closePicker();

    var heart = event.target.closest(HEART_SELECTOR);
    if (heart) {
      event.preventDefault();
      event.stopPropagation();
      toggle(heart);
      return;
    }

    var opener = event.target.closest(OPEN_SELECTOR);
    if (opener) open(event);
  });

  onSwym(function (sdk) {
    swat = sdk || window._swat;
    refresh();

    if (swat && swat.evtLayer && typeof swat.evtLayer.addEventListener === 'function') {
      ['sw:customerInfoRefreshed', 'swym:collections-updated'].forEach(function (name) {
        swat.evtLayer.addEventListener(name, refresh);
      });
    }
  });

  /**
   * Keep the PDP heart pointed at the variant currently selected. The theme
   * usually re-renders the gallery on variant change, but combined listings
   * and morph updates can leave the button in place with stale ids.
   */
  document.addEventListener('variant:update', function (event) {
    var detail = event.detail || {};
    var variant = detail.resource;
    if (!variant) return;

    var newProduct = (detail.data || {}).newProduct;

    document.querySelectorAll('.mb-heart--pdp').forEach(function (el) {
      if (newProduct) {
        el.dataset.empi = String(newProduct.id);
        if (newProduct.url) el.dataset.du = window.location.origin + newProduct.url;
      }
      el.dataset.epi = String(variant.id);
      if (typeof variant.available === 'boolean') {
        el.dataset.stk = String(variant.available);
      }
    });

    render();
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape') closePicker();
  });

  // A removal in the wishlist drawer changes the same list.
  document.addEventListener('wishlist:updated', function (event) {
    if (event.detail && event.detail.source === 'heart') return;
    refresh();
  });

  // Repaint after Section Rendering swaps a product grid in.
  document.addEventListener('shopify:section:load', render);
})();
